import { Component, ElementRef, Inject, NgZone, Input } from '@angular/core';
import { SettingsService } from '../../services/settings/settings.service';
import { FeatureToggleService } from '../../services/feature-toggle/feature-toggle.service';
import { takeUntil } from 'rxjs/operators';
import { BaseObserver } from '../../baseobserver';
import { environment } from '../../../../environments/environment';
import { Util } from '../../../../util';
import { APP_BASE_HREF } from '@angular/common';
import * as moment from 'moment';

const PENDO_UNIDENTIFIED_USER_PREFIX = 'companyUser.';

/**
 * A componentized version of the Pendo approach borrowed from
 * https://ultigit.ultimatesoftware.com/projects/MBL/repos/ultipro-app/browse/src/app/core/services/pendo/pendo.service.ts
 */
@Component({
  selector: 'ssg-pendo',
  template: '',
})
export class PendoComponent extends BaseObserver {
  private initialized = false;
  private pendo: any;
  private _psgInitialized: boolean;

  options: {
    excludeAllText: boolean;
    disableGuides: boolean;
    apiKey: string;
    queryStringWhitelist: any[];
    stagingAgentUrl: string;
    excludeTitle: boolean;
    visitor: { id: string };
    account: { id: string };
  };

  @Input() set psgInitialized(value: boolean) {
    this._psgInitialized = value;
    if (value) {
      this.settings.SettingsInitialized.next();
    }
  }

  get psgInitialized() {
    return this._psgInitialized;
  }

  constructor(private settings: SettingsService,
              private productConfigService: FeatureToggleService,
              private zone: NgZone,
              @Inject(APP_BASE_HREF) private baseHref: string
              ) {
    super();
    this.pendo = window['pendo'];
    this.settings.SettingsInitialized
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.initialize());
  }

  /**
   * Initializes the Pendo JavaScript snippet
   */
  initialize() {
    if (!this.productConfigService.isFeatureEnabled('enablePendo')) {
      return;
    }

    // only initialize the pendo snippet once ever
    if (!this.pendo) {
      this.initPendoSnippet();
    }

    /**
     * These are as per https://ulti.quip.com/6otwAsvVX4AA/Pendo-Integration-getting-started-Info
     */
    this.options = {
      apiKey: environment.pendoApiKey,
      disableGuides: false,
      excludeAllText: true,
      excludeTitle: true,
      queryStringWhitelist: [],
      stagingAgentUrl: Util.prependHost('assets/pendo/pendo.jsonp.js', this.baseHref),
      visitor: {
        id: this.settings.userId || `${PENDO_UNIDENTIFIED_USER_PREFIX}${this.settings.alias}`
      },
      account: {
        id: this.settings.arNumber || this.settings.alias
      }
    };

    try {
      this.zone.runOutsideAngular(() => {
        // In 2.16.5 Pendo changed to ignore subsequent initialize() calls.
        if (!this.initialized) {
          this.pendo.initialize(this.options);
          this.initialized = true;
        }

        this.identify(this.options.visitor, this.options.account);
      });
    } catch (e) {
      console.error(`Failed to initialize Pendo`, {error: e});
    }
  }

  private identify(visitor: any, account: any) {
    if (!this.pendo) {
      return;
    }

    // Explicitly call identify to update the visitor and account
    this.pendo.identify({
      visitor,
      account
    });

    // Attempts to force the Pendo agent to transmit events after API key changes
    this.pendo.flushNow();
  }

  /**
   * Initializes the pendo object and adds it to the DOM. This code is copy/pasted from Pendo.
   */
  private initPendoSnippet() {
    /* eslint-disable */
    ((p, e, n, d, o) => {
      let v, w, x, y, z;
      o = p[d] = p[d] || {};
      o._q = [];
      v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track', 'flushNow'];
      for (w = 0, x = v.length; w < x; ++w) {
        (function (m) {
          o[m] =
            o[m] ||
            function () {
              o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
            };
        })(v[w]);
      }

      y = e.createElement(n);
      y.async = !0;
      y.src = Util.prependHost('assets/pendo/pendo.jsonp.js', this.baseHref);
      z = e.getElementsByTagName(n)[0];
      z.parentNode.insertBefore(y, z);
      this.pendo = window['pendo'];
    })(window, document, 'script', 'pendo');
    /* eslint-enable */
  }
}
